import { Input, SegmentedControl, Tooltip, ActionIcon } from '@mantine/core';
import { IconFunctionFilled, IconLetterS, IconHelp } from '@tabler/icons-react';
import { forwardRef, useMemo } from 'react';
import { extractEmailUsernameSuffixValue } from '~/common/stably-email/destruct-email';
import { emailUsernameFromOrg } from '~/common/stably-email/email-username-from-org';
import type { ExecutorVar } from '~/common/vm/protocol';
import { MonacoInputField } from '~/components/forms/input/MonacoInputField';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import type { ValueType } from '~/server/logic/steps/operation/types';
import { env } from '~/env.mjs';
import type { VariableType } from '~/common/variable/validation';
import { modals } from '@mantine/modals';
import { DynamicVariableGuide } from './DynamicVariableGuide';

// This is controlled, can consider an uncontroleld one later
export const ParameterizedInput = forwardRef<
  HTMLInputElement | HTMLDivElement,
  {
    autoFocus?: boolean;
    disabled?: boolean;
    className?: string;
    type?: string;
    placeholder?: string;
    label?: string | React.ReactNode;
    monacoInputProps?: {
      minLines?: number;
      maxLines?: number;
    };
    readonlyMessage?: string;
    value?: string;
    valueType: ValueType;
    // Make sure the parent memozies this!
    onValueChange: (value: string) => void;
    onValueTypeChange: (valueType: ValueType) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    variables?: ExecutorVar[];
    variableType?: VariableType;
    error?: React.ReactNode;
    onOverlayStateChange?: (hasOverlayOpen: boolean) => void;
    iconSizes?: {
      letterS: number;
      functionFilled: number;
    };
  }
>(
  (
    {
      autoFocus,
      disabled,
      className,
      placeholder,
      type,
      label,
      monacoInputProps,
      readonlyMessage,
      value,
      valueType,
      variableType,
      onValueChange,
      onValueTypeChange,
      onBlur,
      variables,
      // leftSection,
      // leftSectionWidth,
      // rightSection,
      // rightSectionWidth,
      error,
      onOverlayStateChange,
      iconSizes,
    },
    ref,
  ) => {
    const { activeOrg } = useAuthData();
    const emailOrgPrefix = useMemo(() => emailUsernameFromOrg(activeOrg?.orgName || ''), [activeOrg?.orgName]);

    const openVariableGuide = () => {
      onOverlayStateChange?.(true);
      modals.open({
        title: 'Defining Dynamic Variables',
        size: 'lg',
        centered: true,
        children: <DynamicVariableGuide />,
        withOverlay: true,
        overlayProps: {
          backgroundOpacity: 0.55,
          blur: 3,
          zIndex: 10000,
        },
        zIndex: 10001,
        onClose: () => onOverlayStateChange?.(false),
      });
    };

    return (
      <Input.Wrapper
        error={error}
        className={className}
        label={
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <span onClick={(e) => e.preventDefault()}>{label}</span>
            <SegmentedControl
              size="xs"
              className="flex flex-shrink-0 items-center"
              value={valueType}
              onChange={(newValueType) => {
                onValueTypeChange?.(newValueType as ValueType);
              }}
              data={[
                {
                  value: 'STATIC' satisfies ValueType,
                  label: (
                    <Tooltip
                      label="Static Value"
                      position="top"
                    >
                      <IconLetterS size={iconSizes?.letterS || 18} />
                    </Tooltip>
                  ),
                },
                {
                  value: 'DYNAMIC' satisfies ValueType,
                  label: (
                    <Tooltip
                      label="Dynamic Expression"
                      position="top"
                    >
                      <IconFunctionFilled
                        title="Dynamic"
                        size={iconSizes?.functionFilled || 20}
                      />
                    </Tooltip>
                  ),
                },
              ]}
            />
          </div>
        }
        labelProps={{ className: 'w-full text-sm' }}
      >
        <div className="relative">
          {valueType === 'STATIC' ?
            <Input
              type={type}
              placeholder={placeholder}
              key="static"
              autoFocus={autoFocus}
              disabled={disabled}
              leftSection={variableType === 'EMAIL_INBOX' ? `${emailOrgPrefix}+` : undefined}
              leftSectionWidth={variableType === 'EMAIL_INBOX' ? `${emailOrgPrefix}+`.length * 8 + 10 : undefined}
              rightSection={variableType === 'EMAIL_INBOX' ? `@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}` : undefined}
              rightSectionWidth={
                variableType === 'EMAIL_INBOX' ? `@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}`.length * 8 : undefined
              }
              value={
                variableType === 'EMAIL_INBOX' ? extractEmailUsernameSuffixValue(value || '', emailOrgPrefix) : value
              }
              onChange={(event) => {
                const newValue =
                  variableType === 'EMAIL_INBOX' ?
                    `${emailOrgPrefix}+${event.currentTarget.value}@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}`
                  : event.currentTarget.value;
                onValueChange?.(newValue);
              }}
              onBlur={onBlur}
              ref={ref as React.ForwardedRef<HTMLInputElement>}
            />
          : valueType === 'DYNAMIC' ?
            <MonacoInputField
              key={variableType} // Force re-render so that the dynamic editor wouldn't look weird
              readonlyMessage={readonlyMessage}
              autoFocus={autoFocus}
              disabled={disabled}
              leftSection={variableType === 'EMAIL_INBOX' ? `${emailOrgPrefix}+` : undefined}
              leftSectionWidth={variableType === 'EMAIL_INBOX' ? `${emailOrgPrefix}+`.length * 8 + 10 : undefined}
              rightSection={variableType === 'EMAIL_INBOX' ? `@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}` : undefined}
              rightSectionWidth={
                variableType === 'EMAIL_INBOX' ? `@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}`.length * 8 : undefined
              }
              // Disable inner wrapping of the input
              inputWrapperOrder={['input']}
              variables={variables}
              minLines={monacoInputProps?.minLines || (variableType === 'EMAIL_INBOX' ? 1 : 4)}
              maxLines={monacoInputProps?.maxLines || (variableType === 'EMAIL_INBOX' ? 1 : 8)}
              value={
                variableType === 'EMAIL_INBOX' ? extractEmailUsernameSuffixValue(value || '', emailOrgPrefix) : value
              }
              onChange={(event) => {
                const newValue =
                  variableType === 'EMAIL_INBOX' ?
                    `\`${emailOrgPrefix}+\${${event}}@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}\``
                  : `${event}`;
                onValueChange?.(newValue);
              }}
              onBlur={onBlur}
              ref={ref as React.ForwardedRef<HTMLDivElement>}
              // Slight margin to prevent layout shift when switching between static and dynamic
              className="mb-0.5"
            />
          : null}
          {valueType === 'DYNAMIC' && variableType !== 'EMAIL_INBOX' && (
            <Tooltip label="How to define dynamic variables">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                className="absolute bottom-1 right-1"
                onClick={openVariableGuide}
              >
                <IconHelp size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </div>
      </Input.Wrapper>
    );
  },
);

ParameterizedInput.displayName = 'ParameterizedInput';
